"use client";

import type { CmsBottomBarItemFragment } from "@workspace/casino_core/queries/CmsBottombarQuery";
import resolvePhrase from "@workspace/cms-phrases/src/phrase/resolvePhrase";
import { cn, useSidebar } from "@workspace/frontend-ui";
import LinkComponent from "@workspace/nextjs_utils/src/components/LinkComponent";
import { propagateSearchModalToggle } from "@workspace/search-ui";
import { usePathname } from "next/navigation";
import type { FC } from "react";
import DynamicHugeIcon from "../../utils/DynamicHugeIcon";
import { useOpenMenuHref } from "../Sidebar/OpenMenuParam";

type Props = {
  item: CmsBottomBarItemFragment;
  brand: string;
  testId: string;
  onMenu?: () => void;
};

const NavigationItem: FC<Props> = (props) => {
  const currentPath = usePathname();
  const { toggleSidebar, openMobile } = useSidebar();
  const menuHref = useOpenMenuHref();

  const action = props.item.action;
  const isMenu = action === "openMenu";

  const isActive = isMenu
    ? openMobile
    : !!props.item.href && currentPath.indexOf(props.item.href) !== -1;

  const { activeHugeIcon, hugeIcon } = props.item;
  const hasIcons = !!activeHugeIcon && !!hugeIcon;
  const icon = isActive ? activeHugeIcon : hugeIcon;

  const handleClick = () => {
    switch (action) {
      case "openSearch":
        propagateSearchModalToggle?.();
        return;
      case "openMenu":
        toggleSidebar();
        props.onMenu?.();
        return;
      default:
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[BottomNavigationBar] item "${props.item.slug}" has unhandled action "${action}"`,
          );
        }
    }
  };

  const linkProps = {
    className: cn(
      "flex flex-col gap-y-1 items-center py-2 w-full",
      "justify-center rounded-4xl text-foreground",
      isActive && "bg-primary/10",
    ),
    "data-active": isActive ? true : undefined,
    "data-testid": props.testId,
  };
  const linkContent = (
    <div
      className="text-foreground data-active:text-primary flex flex-col justify-center items-center gap-y-1"
      data-active={isActive ? true : undefined}
    >
      {hasIcons && icon && (
        <DynamicHugeIcon
          iconName={icon.iconName}
          iconStyle={icon.iconStyle}
          color={icon.color}
        />
      )}
      <span
        className={cn(
          "whitespace-nowrap text-xs",
          isActive ? "text-primary" : undefined,
        )}
      >
        {resolvePhrase(props.brand, props.item.title)}
      </span>
    </div>
  );

  if (props.item.href) {
    return (
      <LinkComponent href={props.item.href} {...linkProps}>
        {linkContent}
      </LinkComponent>
    );
  }
  // A real link for crawlers (see useOpenMenuHref); a click only opens the menu.
  if (isMenu) {
    return (
      <LinkComponent
        isLocalized
        href={menuHref}
        onClick={(event) => {
          event.preventDefault();
          handleClick();
        }}
        {...linkProps}
      >
        {linkContent}
      </LinkComponent>
    );
  }
  return (
    <div
      className={cn(
        "flex flex-col gap-y-1 items-center py-2 w-full flex flex-col justify-center items-center gap-y-1",
        "justify-center rounded-4xl text-foreground",
        isActive && "bg-primary/10",
      )}
      key={props.item.slug}
      onClick={action ? handleClick : undefined}
      data-testid={props.testId}
    >
      {hasIcons && icon && (
        <DynamicHugeIcon
          iconName={icon.iconName}
          iconStyle={icon.iconStyle}
          color={icon.color}
        />
      )}
      <span
        className={cn(
          "whitespace-nowrap text-xs",
          isActive ? "text-primary" : undefined,
        )}
      >
        {resolvePhrase(props.brand, props.item.title)}
      </span>
    </div>
  );
};

export default NavigationItem;
